import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { logger } from '@/shared/utils/logger';
import { captureException } from '@/utils/sentry';
import {
  SWISS_CARD,
  SWISS_TYPOGRAPHY,
  SWISS_TOUCH,
  SWISS_TRANSITIONS,
  swissClass
} from '@/shared/utils/swissStyle';

interface Props {
  children: ReactNode;
  fallbackMessage?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * ErrorBoundary - Cattura errori React e mostra UI di fallback
 *
 * Previene crash dell'app e permette recovery graceful
 * Swiss Style design con opzione per ricaricare
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console and logger
    logger.error('ErrorBoundary caught an error:', error, errorInfo);

    this.setState({
      error,
      errorInfo
    });

    // Send to Sentry error reporting
    captureException(error, {
      componentStack: errorInfo.componentStack || undefined,
    });
  }

  handleReload = () => {
    // Reset error state and reload
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });

    // Full page reload as fallback
    window.location.reload();
  };

  handleReset = () => {
    // Just reset the error state without reloading
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
          <div className={swissClass(SWISS_CARD.base, 'max-w-2xl w-full p-8')}>
            {/* Error Icon */}
            <div className="flex justify-center mb-6">
              <div className="p-4 rounded-full bg-red-100">
                <AlertTriangle className="w-12 h-12 text-red-600" />
              </div>
            </div>

            {/* Error Message */}
            <h1 className={swissClass(SWISS_TYPOGRAPHY.headingLG, 'text-center mb-4')}>
              Si è verificato un errore
            </h1>

            <p className={swissClass(SWISS_TYPOGRAPHY.body, 'text-center text-gray-600 mb-6')}>
              {this.props.fallbackMessage ||
                'Qualcosa è andato storto. Ricarica la pagina per continuare.'}
            </p>

            {/* Error Details (Development only) */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mb-6 p-4 bg-gray-100 rounded border-2 border-gray-200">
                <summary className={swissClass(
                  SWISS_TYPOGRAPHY.bodySM,
                  'cursor-pointer font-medium mb-2'
                )}>
                  Dettagli errore (solo in development)
                </summary>
                <div className="mt-4">
                  <p className="font-mono text-sm text-red-600 mb-2">
                    {this.state.error.toString()}
                  </p>
                  {this.state.errorInfo && (
                    <pre className="font-mono text-xs text-gray-700 overflow-auto max-h-64 p-2 bg-white rounded border">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  )}
                </div>
              </details>
            )}

            {/* Actions */}
            <div className="flex gap-4 justify-center">
              <button
                onClick={this.handleReset}
                className={swissClass(
                  SWISS_TOUCH.min,
                  SWISS_TRANSITIONS.colors,
                  'px-6 py-3 border-2 border-gray-300 text-gray-700',
                  'font-medium uppercase tracking-wide text-sm',
                  'hover:bg-gray-50 transition-colors'
                )}
              >
                Riprova
              </button>

              <button
                onClick={this.handleReload}
                className={swissClass(
                  SWISS_TOUCH.min,
                  SWISS_TRANSITIONS.colors,
                  'px-6 py-3 border-2 border-gray-900 bg-gray-900 text-white',
                  'font-medium uppercase tracking-wide text-sm',
                  'hover:bg-gray-800 hover:border-gray-800 transition-colors',
                  'flex items-center gap-2'
                )}
              >
                <RefreshCw className="w-4 h-4" />
                Ricarica Pagina
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
